import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Divider,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowForward, QrCodeScanner, ShieldOutlined, StorefrontOutlined } from '@mui/icons-material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useAuth } from '@/auth/AuthProvider';
import { useI18n } from '@/i18n';

export default function LoginPage() {
  const { login } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [email, setEmail] = useState('marcin');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const highlights = [
    {
      labelKey: 'login.scanFeatureTitle',
      descriptionKey: 'login.scanFeatureDescription',
      icon: <QrCodeScanner />,
    },
    {
      labelKey: 'login.warehouseFeatureTitle',
      descriptionKey: 'login.warehouseFeatureDescription',
      icon: <StorefrontOutlined />,
    },
    {
      labelKey: 'login.securityFeatureTitle',
      descriptionKey: 'login.securityFeatureDescription',
      icon: <ShieldOutlined />,
    },
  ];

  const handleSubmit = async () => {
    setLoading(true);
    setErrorMessage('');

    try {
      const user = await login(email, password);
      navigate(user.role === 'Admin' || user.role === 'Manager' ? '/dashboard' : '/scan', { replace: true });
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : t('login.submitError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        px: { xs: 1.5, sm: 3 },
        py: { xs: 2, md: 4 },
        display: 'grid',
        placeItems: 'center',
        background:
          'radial-gradient(ellipse at 8% 8%, rgba(56,189,248,0.17), transparent 34%), radial-gradient(ellipse at 94% 8%, rgba(34,197,94,0.12), transparent 30%), linear-gradient(145deg, #f8fbff 0%, #edf3fa 58%, #f5f8fc 100%)',
      }}
    >
      <Container maxWidth="lg" sx={{ px: { xs: 0, sm: 2 } }}>
          <Grid container spacing={{ xs: 2, md: 3 }} alignItems="stretch">
            <Grid item xs={12} lg={7} sx={{ minWidth: 0 }}>
              <Card
                sx={{
                  height: '100%',
                  minHeight: { lg: 620 },
                  overflow: 'hidden',
                  borderRadius: { xs: 4, md: 5 },
                  color: '#fff',
                  background: 'linear-gradient(135deg, #143f6b 0%, #0f766e 100%)',
                  boxShadow: '0 28px 70px rgba(16,47,81,0.2)',
                }}
              >
                <CardContent sx={{ p: { xs: 2.5, sm: 3.5, md: 4.5 }, height: '100%' }}>
                  <Stack spacing={{ xs: 3, md: 3.5 }} sx={{ height: '100%' }}>
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Box
                        component={RouterLink}
                        to="/scan"
                        aria-label="Otwórz skaner Variboxów"
                        sx={{ display: 'block', lineHeight: 0, borderRadius: 3, flexShrink: 0, '&:focus-visible': { outline: '3px solid #a8ec65', outlineOffset: 4 } }}
                      >
                        <Box
                        component="img"
                        src="/myzenor-logo.png"
                        alt="myZenor"
                        sx={{
                          width: { xs: 112, sm: 128, md: 144 },
                          height: { xs: 112, sm: 128, md: 144 },
                          objectFit: 'contain',
                          p: 0,
                          borderRadius: 3,
                          bgcolor: '#fff',
                          boxShadow: '0 8px 20px rgba(4,18,40,0.14)',
                          flexShrink: 0,
                        }}
                      />
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="overline" sx={{ opacity: 0.8, letterSpacing: 1.6, fontWeight: 700 }}>
                          {t('login.platformLabel')}
                        </Typography>
                        <Typography variant="h4" sx={{ fontWeight: 850, lineHeight: 1.08, fontSize: { xs: '1.65rem', sm: '2.1rem' } }}>
                          {t('login.productTitle')}
                        </Typography>
                        <Typography
                          component="a"
                          href="mailto:marcin.chojan@icloud.com"
                          aria-label="Napisz do myZenor"
                          variant="body2"
                          sx={{ mt: 0.5, opacity: 0.84, color: 'inherit', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                        >
                          {t('login.byline')}
                        </Typography>
                      </Box>
                    </Stack>

                    <Box>
                      <Typography variant="h6" sx={{ maxWidth: 650, fontWeight: 500, lineHeight: 1.6, opacity: 0.94 }}>
                        {t('login.productDescription')}
                      </Typography>
                    </Box>

                    <Grid container spacing={1.5} sx={{ flexGrow: 1, alignContent: 'stretch' }}>
                      {highlights.map((item) => (
                        <Grid item xs={12} sm={4} key={item.labelKey} sx={{ minWidth: 0, display: 'flex' }}>
                          <Box sx={{ width: '100%', p: { xs: 1.75, sm: 2 }, borderRadius: 3, bgcolor: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.14)' }}>
                            <Stack spacing={1.25}>
                              <Box sx={{ width: 36, height: 36, display: 'grid', placeItems: 'center', borderRadius: 2.5, color: '#fff', bgcolor: 'rgba(255,255,255,0.15)' }}>
                                {item.icon}
                              </Box>
                              <Typography variant="subtitle2" sx={{ fontWeight: 800, lineHeight: 1.25 }}>{t(item.labelKey)}</Typography>
                              <Typography variant="body2" sx={{ opacity: 0.85, lineHeight: 1.5, fontSize: 13 }}>{t(item.descriptionKey)}</Typography>
                            </Stack>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>

                    <Typography variant="caption" sx={{ opacity: 0.78 }}>
                      {t('login.secureAccess')}
                    </Typography>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} lg={5} sx={{ minWidth: 0, display: 'flex' }}>
              <Card sx={{ width: '100%', minHeight: { lg: 620 }, borderRadius: { xs: 4, md: 5 }, boxShadow: '0 24px 60px rgba(16,47,81,0.1)' }}>
                <CardContent sx={{ p: { xs: 2.5, sm: 3.5, md: 4 }, height: '100%', display: 'flex' }}>
                  <Stack spacing={2.5} sx={{ width: '100%' }}>
                    <Box>
                      <Typography variant="overline" color="primary.main" sx={{ letterSpacing: 1.5, fontWeight: 800 }}>{t('login.welcomeBack')}</Typography>
                      <Typography variant="h4" sx={{ mt: 0.5, fontWeight: 850, letterSpacing: '-0.03em' }}>{t('login.signInTitle')}</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1, lineHeight: 1.6 }}>
                        {t('login.signInDescription')}
                      </Typography>
                    </Box>

                    {errorMessage ? <Alert severity="error" variant="outlined">{errorMessage}</Alert> : null}

                    <Stack
                      component="form"
                      spacing={2}
                      onSubmit={(event) => {
                        event.preventDefault();
                        void handleSubmit();
                      }}
                    >
                      <TextField
                        label={t('login.loginLabel')}
                        value={email}
                        onChange={(event) => setEmail(event.target.value)}
                        autoComplete="username"
                        autoFocus
                        fullWidth
                      />
                      <TextField
                        label={t('login.passwordLabel')}
                        type="password"
                        value={password}
                        onChange={(event) => setPassword(event.target.value)}
                        autoComplete="current-password"
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') {
                            event.preventDefault();
                            void handleSubmit();
                          }
                        }}
                        fullWidth
                      />
                      <Button
                        variant="contained"
                        type="submit"
                        size="large"
                        endIcon={<ArrowForward />}
                        disabled={loading || !email.trim() || !password}
                        sx={{ minHeight: 50, borderRadius: 3, fontWeight: 800 }}
                      >
                        {loading ? t('login.submitting') : t('login.submit')}
                      </Button>
                    </Stack>

                    <Divider />

                    <Box sx={{ mt: 'auto !important', p: 2, borderRadius: 3, bgcolor: 'rgba(20,63,107,0.045)' }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>{t('login.noAccount')}</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, lineHeight: 1.55 }}>
                        {t('login.noAccountDescription')}
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', pt: 2 }}>
            {t('login.footerBrand')} <Box component="span" sx={{ mx: 0.5 }}>·</Box> by{' '}
            <Box component="a" href="mailto:marcin.chojan@icloud.com" sx={{ color: 'secondary.main', fontWeight: 800, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
              myZenor
            </Box>
          </Typography>
      </Container>
    </Box>
  );
}